"use client";

import React, { useRef, useState } from "react";
import { FolderOpen, Upload, Link2, X, Loader2, ImageIcon, Search } from "lucide-react";
import { FilePickerModal } from "./FilePickerModal";
import { ImageLightbox } from "./ImageLightbox";

interface ImageSlotProps {
    /** 当前已有图片 URL（签名 URL，用于预览） */
    src?: string;
    /** 上传完成后回调 */
    onAdd: (url: string) => void;
    /** 点击删除按钮回调 */
    onRemove: () => void;
    accept?: string;
    disabled?: boolean;
}

/**
 * 单个图片槽位
 * - 空状态：显示加号占位
 * - 有图：显示缩略图
 * - Hover：3 个操作按钮浮现（历史选择 / 本地上传 / URL 获取）
 */
export const ImageSlot: React.FC<ImageSlotProps> = ({
    src,
    onAdd,
    onRemove,
    accept = "image/jpeg,image/png,image/webp",
    disabled = false,
}) => {
    const fileInputRef = useRef<HTMLInputElement>(null);
    const [showModal, setShowModal] = useState(false);
    const [showUrlInput, setShowUrlInput] = useState(false);
    const [urlValue, setUrlValue] = useState("");
    const [isFetching, setIsFetching] = useState(false);
    const [isUploading, setIsUploading] = useState(false);
    const [showLightbox, setShowLightbox] = useState(false);

    const handleLocalUpload = async (files: FileList | null) => {
        if (!files || files.length === 0) return;
        setIsUploading(true);
        const fd = new FormData();
        fd.append("file", files[0]);
        try {
            const res = await fetch("/api/upload/product", { method: "POST", body: fd });
            if (res.ok) {
                const data = await res.json();
                onAdd(data.url);
            }
        } finally {
            setIsUploading(false);
        }
    };

    const handleUrlFetch = async () => {
        if (!urlValue.trim()) return;
        setIsFetching(true);
        try {
            const res = await fetch("/api/upload/from-url", {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ url: urlValue.trim() }),
            });
            if (res.ok) {
                const data = await res.json();
                onAdd(data.url);
                setShowUrlInput(false);
                setUrlValue("");
            }
        } finally {
            setIsFetching(false);
        }
    };

    const isLoading = isUploading || isFetching;

    return (
        <>
            <div className="relative w-40 h-40 group rounded-lg overflow-hidden border border-zinc-200 dark:border-zinc-700 bg-zinc-100 dark:bg-zinc-800 shrink-0 shadow-sm transition-all hover:border-purple-500/50">
                {/* Content */}
                {isLoading ? (
                    <div className="w-full h-full flex items-center justify-center">
                        <Loader2 size={18} className="animate-spin text-purple-500" />
                    </div>
                ) : src ? (
                    <img src={src} alt="" className="w-full h-full object-cover" />
                ) : (
                    <div className="w-full h-full flex flex-col items-center justify-center gap-1 text-zinc-400 dark:text-zinc-600">
                        <ImageIcon size={20} strokeWidth={1.5} />
                        <span className="text-[9px]">添加图片</span>
                    </div>
                )}

                {/* Hover overlay — action buttons */}
                {!disabled && !isLoading && (
                    <div className="absolute inset-0 bg-black/55 opacity-0 group-hover:opacity-100 transition-opacity flex flex-col items-center justify-center gap-1 p-1.5">
                        {/* 查看大图 (Zoom) */}
                        {src && (
                            <button
                                title="查看大图"
                                onClick={() => setShowLightbox(true)}
                                className="w-full flex items-center gap-1.5 px-1.5 py-1 text-[10px] text-white bg-purple-600 hover:bg-purple-500 rounded transition-colors mb-0.5"
                            >
                                <Search size={10} /> 放大预览 (Zoom)
                            </button>
                        )}

                        <div className="grid grid-cols-1 w-full gap-1">
                            {/* 历史选择 */}
                            <button
                                title="历史文件"
                                onClick={() => setShowModal(true)}
                                className="w-full flex items-center gap-1.5 px-1.5 py-1 text-[10px] text-white bg-white/15 hover:bg-zinc-700/80 rounded transition-colors text-left"
                            >
                                <FolderOpen size={10} /> 历史库
                            </button>

                            {/* 本地上传 */}
                            <button
                                title="上传本地文件"
                                onClick={() => fileInputRef.current?.click()}
                                className="w-full flex items-center gap-1.5 px-1.5 py-1 text-[10px] text-white bg-white/15 hover:bg-zinc-700/80 rounded transition-colors text-left"
                            >
                                <Upload size={10} /> 上传
                            </button>
                        </div>

                        {/* URL 获取 */}
                        <button
                            title="URL 获取"
                            onClick={() => setShowUrlInput(v => !v)}
                            className="w-full flex items-center gap-1.5 px-1.5 py-1 text-[10px] text-zinc-400 hover:text-white transition-colors mt-auto"
                        >
                            <Link2 size={10} /> URL 链接
                        </button>

                        {/* Remove (only when image exists) */}
                        {src && (
                            <button
                                title="移除"
                                onClick={onRemove}
                                className="absolute top-1 right-1 bg-black/60 text-white rounded-full p-0.5 hover:bg-red-500 transition-colors"
                            >
                                <X size={9} />
                            </button>
                        )}
                    </div>
                )}
            </div>

            {/* URL 快捷输入框（悬浮在 slot 下方） */}
            {showUrlInput && (
                <div className="absolute z-50 mt-1 bg-white dark:bg-zinc-800 border border-zinc-200 dark:border-zinc-700 rounded-xl shadow-xl p-3 w-72">
                    <p className="text-[10px] text-zinc-500 mb-1.5 font-medium">输入图片 URL</p>
                    <div className="flex gap-1.5">
                        <input
                            autoFocus
                            type="url"
                            value={urlValue}
                            onChange={(e) => setUrlValue(e.target.value)}
                            onKeyDown={(e) => { if (e.key === "Enter") handleUrlFetch(); if (e.key === "Escape") setShowUrlInput(false); }}
                            placeholder="https://..."
                            className="flex-1 px-2 py-1.5 text-xs bg-zinc-50 dark:bg-zinc-700 border border-zinc-200 dark:border-zinc-600 rounded-lg outline-none focus:border-purple-400 text-zinc-800 dark:text-zinc-200"
                        />
                        <button
                            onClick={handleUrlFetch}
                            disabled={isFetching}
                            className="px-2.5 py-1.5 bg-purple-600 hover:bg-purple-500 text-white text-xs rounded-lg disabled:opacity-50"
                        >
                            {isFetching ? <Loader2 size={10} className="animate-spin" /> : "获取"}
                        </button>
                    </div>
                </div>
            )}

            {/* Hidden file input */}
            <input
                ref={fileInputRef}
                type="file"
                accept={accept}
                className="sr-only"
                onChange={(e) => handleLocalUpload(e.target.files)}
            />

            {/* File picker modal */}
            {showModal && (
                <FilePickerModal
                    accept={accept}
                    onSelect={(url) => { onAdd(url); setShowModal(false); }}
                    onClose={() => setShowModal(false)}
                />
            )}
            {/* Lightbox for zoom */}
            {showLightbox && src && (
                <ImageLightbox src={src} onClose={() => setShowLightbox(false)} />
            )}
        </>
    );
};
